import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Select, { StyledExpansitionIconContainer } from 'UIKit/Select'
import GameFilterOptionItem from './components/GameFilterOptionItem'
import GameFilterSelectedOption from './components/GameFilterSelectedOption'
import { GameFilterOptionType } from './config'
import { useOpenGameFilterModal } from './hooks'

type GameFilterProps = {
  selectedOption: GameFilterOptionType
  onSelectOption: (option: GameFilterOptionType) => void
  filterOptions: GameFilterOptionType[]
}

const GamesFilter: React.FC<GameFilterProps & BoxProps> = ({
  selectedOption,
  onSelectOption,
  filterOptions,
  ...props
}) => {
  const options = useMemo(
    () => (selectedOption ? filterOptions.filter((item) => item.key !== selectedOption.key) : filterOptions),
    [filterOptions, selectedOption],
  )
  const { handlePresentGameFilterModal } = useOpenGameFilterModal()
  const { isMobile } = useMatchBreakpoints()

  const handleOpenGameFilterModal = () => {
    handlePresentGameFilterModal({
      selectedOption,
      onSelectOption,
      filterOptions,
    })
  }

  return (
    <WrapperFilter minWidth={['120px', '120px', '140px']} {...props}>
      {isMobile ? (
        <Flex height="100%" alignItems="center" p="0 10px" onClick={handleOpenGameFilterModal}>
          <Flex flex="1 1" width="100%">
            {selectedOption && <GameFilterSelectedOption value={selectedOption.value} />}
          </Flex>

          <Icons.ArrowDownIcon fill={colors.textSubtle} />
        </Flex>
      ) : (
        <StyledSelect
          options={options}
          onSelect={onSelectOption}
          OptionItemComponent={GameFilterOptionItem}
          dropdownContentProps={{
            className: 'select-dropdown',
          }}
          className="select-container"
        >
          {selectedOption && <GameFilterSelectedOption value={selectedOption.value} />}
        </StyledSelect>
      )}
    </WrapperFilter>
  )
}

const StyledSelect = styled(Select)`
  padding: 8px 10px;

  ${StyledExpansitionIconContainer} {
    padding: 0;
    height: 20px;
    margin: 0;
  }
`

const WrapperFilter = styled(Box)`
  background: ${({ theme }) => theme.colors.card_primary};
  border-radius: ${({ theme }) => theme.radii.button};
  border: none;

  --option-items-radius: ${({ theme }) => theme.radii.button};
  --option-inner-container-padding: 8px;
  --option-inner-container-radius: calc(var(--option-items-radius) + var(--option-inner-container-padding));

  .select-dropdown {
    box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.4);
    background: ${({ theme }) => theme.colors.bg_surface};
    border-radius: var(--option-inner-container-radius);
    padding: var(--option-inner-container-padding);
    border: 1px solid ${({ theme }) => theme.colors.buttonBackgroundAlt2};
  }

  .options-inner-container {
    margin: 0;
  }

  .option-items {
    border-radius: var(--option-items-radius);

    &:hover {
      background: ${({ theme }) => theme.colors.card_primary_active_alt} !important;
    }
  }
`

export default GamesFilter
